/**
 * Icebox fulfillment type — private line item property `_icebox_fulfillment_type`.
 *
 * Values: 'in_stock' | 'made_to_order'
 *
 * Rule (single source of truth, see classify()):
 *   A cart line is in_stock when inventory is not tracked (null from the
 *   stock endpoint — bulk SKU, always ships now) OR when inventory is
 *   tracked and the available quantity covers the TOTAL quantity of that
 *   variant across every line in the cart (after the add/change being made).
 *   Missing or non-numeric stock (other than null) is made_to_order.
 *   All lines sharing a variant therefore carry the same value.
 *
 * Inventory source: sections/icebox-fulfillment-stock.liquid rendered through
 * the Section Rendering API. It is fetched fresh at the moment of each
 * add / quantity change / drawer open / checkout click, so the estimate is as
 * current as the storefront can make it. It is still a storefront estimate:
 * it does not reserve inventory and cannot see orders placed after it ran.
 *
 * All existing properties on a line (Ring Size, Letters, Number, Character
 * Count, gift card fields, ...) are preserved: change.js replaces the whole
 * properties object, so we always send the existing set plus our key.
 */
(function () {
  const PROPERTY = '_icebox_fulfillment_type';
  const IN_STOCK = 'in_stock';
  const MADE_TO_ORDER = 'made_to_order';
  const SECTION_ID = 'icebox-fulfillment-stock';
  const REQUEST_TIMEOUT_MS = 4000;

  const cartUrl = () => (window.routes && window.routes.cart_url) || '/cart';
  const changeUrl = () => (window.routes && window.routes.cart_change_url) || '/cart/change';

  function classify(stock, totalQuantity) {
    const qty = Number(totalQuantity);
    if (!Number.isFinite(qty) || qty <= 0) return MADE_TO_ORDER;
    // null = inventory not tracked (bulk SKU). Always in stock.
    if (stock === null) return IN_STOCK;
    if (typeof stock !== 'number' || !Number.isFinite(stock)) return MADE_TO_ORDER;
    return stock >= qty ? IN_STOCK : MADE_TO_ORDER;
  }

  function withTimeout(promise, ms) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('icebox-fulfillment: timeout')), ms);
      promise.then(
        (value) => {
          clearTimeout(timer);
          resolve(value);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  function fetchStock(baseUrl) {
    const url = new URL(baseUrl || cartUrl(), window.location.origin);
    url.searchParams.set('section_id', SECTION_ID);
    // Cache-buster: some CDNs/browsers cache section renders briefly.
    url.searchParams.set('_t', Date.now().toString(36));
    return fetch(url.toString(), { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error(`icebox-fulfillment: stock ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const start = html.indexOf('{');
        const end = html.lastIndexOf('}');
        if (start === -1 || end === -1) throw new Error('icebox-fulfillment: no JSON in section');
        const parsed = JSON.parse(html.slice(start, end + 1));
        return parsed && parsed.stock ? parsed.stock : {};
      });
  }

  // Coalesce concurrent /cart.js reads: prepareAdd/prepareChange and a drawer
  // reconcile that overlap in the same moment share one in-flight request
  // instead of each issuing their own (D-09). An in-flight response reflects a
  // single point in time, so this never serves stale data — it only dedupes
  // requests that are already outstanding, and clears as soon as it settles.
  let cartInFlight = null;
  function fetchCart() {
    if (cartInFlight) return cartInFlight;
    cartInFlight = fetch(`${cartUrl()}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`icebox-fulfillment: cart ${response.status}`);
        return response.json();
      })
      .finally(() => {
        cartInFlight = null;
      });
    return cartInFlight;
  }

  function totalsByVariant(items) {
    const totals = {};
    items.forEach((item) => {
      const key = String(item.variant_id);
      totals[key] = (totals[key] || 0) + Number(item.quantity || 0);
    });
    return totals;
  }

  function withProperty(properties, value) {
    const next = Object.assign({}, properties || {});
    next[PROPERTY] = value;
    return next;
  }

  /**
   * POST /cart/change.js for one line, identified by its key (stable across
   * index shifts caused by merges). Returns the parsed cart response.
   */
  function changeLine(payload, extra) {
    const body = Object.assign({}, payload, extra || {});
    return fetch(changeUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => response.json());
  }

  /**
   * Builds the list of change.js payloads needed so that every line of
   * `variantId` carries `desired`. Skips lines already correct.
   */
  function alignmentChanges(items, variantId, desired) {
    return items
      .filter((item) => String(item.variant_id) === String(variantId))
      .filter((item) => !item.properties || item.properties[PROPERTY] !== desired)
      .map((item) => ({
        id: item.key,
        quantity: item.quantity,
        properties: withProperty(item.properties, desired),
      }));
  }

  function runSequentially(payloads, extra) {
    return payloads.reduce(
      (chain, payload, index) =>
        chain.then(() => changeLine(payload, index === payloads.length - 1 ? extra : undefined)),
      Promise.resolve(null)
    );
  }

  /**
   * Called by product-form.js right before /cart/add.js.
   *
   * Computes the value for the line about to be added (existing cart quantity
   * of the same variant + the quantity being added), aligns any existing
   * lines of that variant so Shopify can merge identical lines, and returns
   * the value to put in properties[_icebox_fulfillment_type].
   *
   * Never rejects: on any failure it resolves to `fallback` (the Liquid
   * rendered default from the form) so the add is never blocked.
   */
  function prepareAdd({ variantId, quantity, productUrl, fallback }) {
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    if (!variantId) return Promise.resolve(fallback || MADE_TO_ORDER);

    const work = Promise.all([fetchStock(productUrl || window.location.pathname), fetchCart()]).then(
      ([stock, cart]) => {
        const totals = totalsByVariant(cart.items || []);
        const total = (totals[String(variantId)] || 0) + qty;
        const desired = classify(stock[String(variantId)], total);
        const changes = alignmentChanges(cart.items || [], variantId, desired);
        return runSequentially(changes).then(() => desired);
      }
    );

    return withTimeout(work, REQUEST_TIMEOUT_MS).catch((error) => {
      console.warn(error);
      return fallback || MADE_TO_ORDER;
    });
  }

  /**
   * Called by cart.js before /cart/change.js for a quantity change (or a
   * removal when quantity is 0) on the line with `key` (or 1-based `line`).
   *
   * Resolves to { properties, followUps } where `properties` is the full
   * property set the changed line should carry (or null when it needs no
   * change), and `followUps` are change.js payloads for the OTHER lines of
   * the same variant that must flip as a result. Never rejects.
   */
  function prepareChange({ line, key, quantity }) {
    const nextQty = Math.max(0, parseInt(quantity, 10) || 0);

    const work = Promise.all([fetchStock(cartUrl()), fetchCart()]).then(([stock, cart]) => {
      const items = cart.items || [];
      const target = key ? items.find((item) => item.key === key) : items[Number(line) - 1];
      if (!target) return { properties: null, followUps: [] };

      const variantKey = String(target.variant_id);
      const total = items.reduce((sum, item) => {
        if (String(item.variant_id) !== variantKey) return sum;
        return sum + (item === target ? nextQty : Number(item.quantity || 0));
      }, 0);
      const desired = classify(stock[variantKey], total);

      const properties =
        nextQty > 0 && (!target.properties || target.properties[PROPERTY] !== desired)
          ? withProperty(target.properties, desired)
          : null;

      const followUps = alignmentChanges(
        items.filter((item) => item !== target),
        target.variant_id,
        desired
      );

      return { properties, followUps };
    });

    return withTimeout(work, REQUEST_TIMEOUT_MS).catch((error) => {
      console.warn(error);
      return { properties: null, followUps: [] };
    });
  }

  /**
   * Applies follow-up payloads sequentially. `extra` (e.g. { sections,
   * sections_url }) is attached to the last request so the caller can paint
   * the final cart state. Resolves to the last cart response, or null.
   */
  function applyFollowUps(followUps, extra) {
    if (!followUps || !followUps.length) return Promise.resolve(null);
    return runSequentially(followUps, extra).catch((error) => {
      console.warn(error);
      return null;
    });
  }

  let reconcileInFlight = null;

  /**
   * Re-evaluates every line in the cart against fresh inventory and fixes any
   * line whose property is missing or stale (e.g. carts from a previous
   * session, permalinks, lines added while another shopper bought the last
   * unit). Resolves to { changed, cart } and never rejects. Concurrent calls
   * share one in-flight run.
   *
   * @param {Object} [extra] merged into each change.js payload (e.g. sections)
   * @param {Object} [options]
   * @param {number} [options.timeoutMs] cap on the whole run (default 8s)
   */
  function reconcileCart(extra, options) {
    if (reconcileInFlight) return reconcileInFlight;
    const timeoutMs = (options && options.timeoutMs) || REQUEST_TIMEOUT_MS * 2;

    const work = Promise.all([fetchStock(cartUrl()), fetchCart()]).then(([stock, cart]) => {
      const items = cart.items || [];
      if (!items.length) return { changed: false, cart };

      const totals = totalsByVariant(items);
      const seen = new Set();
      let payloads = [];
      items.forEach((item) => {
        const key = String(item.variant_id);
        if (seen.has(key)) return;
        seen.add(key);
        const desired = classify(stock[key], totals[key]);
        payloads = payloads.concat(alignmentChanges(items, item.variant_id, desired));
      });

      if (!payloads.length) return { changed: false, cart };
      return runSequentially(payloads, extra).then((last) => ({ changed: true, cart: last || cart }));
    });

    reconcileInFlight = withTimeout(work, timeoutMs)
      .catch((error) => {
        console.warn(error);
        return { changed: false, cart: null };
      })
      .finally(() => {
        reconcileInFlight = null;
      });

    return reconcileInFlight;
  }

  window.IceboxFulfillment = {
    PROPERTY,
    IN_STOCK,
    MADE_TO_ORDER,
    classify,
    fetchStock,
    prepareAdd,
    prepareChange,
    applyFollowUps,
    reconcileCart,
  };
})();
